import type {MixedListRow} from '@github-ui/shared-dashboard-lists/issues/IssuesList'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {useCallback, useMemo} from 'react'

import {useRepoIssuesBulkActions} from './use-repo-issues-bulk-actions'

interface UseRepoIssuesBulkActionStateArgs {
  owner: string
  repo: string
  currentPage: number
  query: string
  repositoryId: string
  currentUserId: string
  viewerCanBulkUpdate: boolean
  totalCount: number
  issues: MixedListRow[]
  hasProjectsEnabled: boolean
  issueTypesEnabled: boolean
  refreshIssues: () => Promise<{isError: boolean; error: unknown}>
}

export function useRepoIssuesBulkActionState({
  owner,
  repo,
  currentPage,
  query,
  repositoryId,
  currentUserId,
  viewerCanBulkUpdate,
  totalCount,
  issues,
  hasProjectsEnabled,
  issueTypesEnabled,
  refreshIssues,
}: UseRepoIssuesBulkActionStateArgs) {
  const issueRows = useMemo(() => issues.filter(issue => issue.itemType === 'issue'), [issues])
  const bulkJobStorageKey = `issues.repo-bulk-job:v1:${currentUserId}:${repositoryId}`
  const [bulkJobId, setBulkJobId, clearBulkJobId] = useLocalStorage<string | null>(bulkJobStorageKey, null)
  const onBulkJobStarted = useCallback((jobId: string) => setBulkJobId(jobId, {flush: true}), [setBulkJobId])
  const bulkActions = useRepoIssuesBulkActions({
    owner,
    repo,
    currentPage,
    query,
    repositoryId,
    viewerCanBulkUpdate,
    totalCount,
    issueRows,
    allRowsAreIssues: issueRows.length > 0 && issueRows.length === issues.length,
    hasProjectsEnabled,
    issueTypesEnabled,
    refreshIssues,
    bulkJobPending: Boolean(bulkJobId),
    onBulkJobStarted,
    clearBulkJob: clearBulkJobId,
  })

  return {bulkJobId, clearBulkJobId, ...bulkActions}
}
